import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { supabase } from '@/lib/supabase';
import {
  ScanLine,
  Camera,
  CameraOff,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RotateCcw,
  Search,
  ExternalLink,
  Laptop as LaptopIcon,
  Package,
  Wrench,
} from 'lucide-react';

interface ScanResult {
  type: 'laptop' | 'accessory' | 'repair' | 'unknown';
  id: string | null;
  label: string;
  subLabel: string;
  url: string | null;
}

export const ScannerPage: React.FC = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReader = useRef<BrowserMultiFormatReader | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);

  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastScanned, setLastScanned] = useState<string | null>(null);
  const [isLooking, setIsLooking] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [manualInput, setManualInput] = useState('');

  // Resolve a scanned code → Supabase entity lookup
  const resolveCode = useCallback(async (code: string) => {
    if (!code.trim()) return;
    setIsLooking(true);
    setResult(null);

    try {
      // 1. Check if it's an AKT QR payload (AKT1:L:<uuid>)
      if (code.startsWith('AKT1:L:')) {
        const uuid = code.replace('AKT1:L:', '').trim();
        const { data: laptopByUuid } = await supabase
          .from('laptops')
          .select('id, stock_id, brand, model, status')
          .eq('id', uuid)
          .maybeSingle();

        if (laptopByUuid) {
          setResult({
            type: 'laptop',
            id: laptopByUuid.id,
            label: `${laptopByUuid.brand} ${laptopByUuid.model}`,
            subLabel: `Stock ID: ${laptopByUuid.stock_id} • Status: ${laptopByUuid.status}`,
            url: `/app/inventory/laptops`,
          });
          return;
        }
      }

      // 2. Check if it's a laptop stock_id, serial, or barcode
      const { data: laptopById } = await supabase
        .from('laptops')
        .select('id, stock_id, brand, model, status')
        .or(`stock_id.ilike.${code},serial_number.ilike.${code},barcode.ilike.${code},service_tag.ilike.${code}`)
        .limit(1)
        .maybeSingle();

      if (laptopById) {
        setResult({
          type: 'laptop',
          id: laptopById.id,
          label: `${laptopById.brand} ${laptopById.model}`,
          subLabel: `Stock ID: ${laptopById.stock_id} • Status: ${laptopById.status}`,
          url: `/app/inventory/laptops`,
        });
        return;
      }

      // 2. Check if it's an accessory SKU
      const { data: accessory } = await supabase
        .from('accessories')
        .select('id, sku, name, quantity')
        .eq('sku', code)
        .limit(1)
        .single();

      if (accessory) {
        setResult({
          type: 'accessory',
          id: accessory.id,
          label: accessory.name,
          subLabel: `SKU: ${accessory.sku} • Qty in stock: ${accessory.quantity}`,
          url: `/app/inventory/accessories`,
        });
        return;
      }

      // 3. Check repair job_number (QR on repair ticket)
      const { data: repair } = await supabase
        .from('repair_jobs')
        .select('id, job_number, status, priority')
        .eq('job_number', code)
        .limit(1)
        .single();

      if (repair) {
        setResult({
          type: 'repair',
          id: repair.id,
          label: `Repair Job #${repair.job_number}`,
          subLabel: `Status: ${repair.status} • Priority: ${repair.priority}`,
          url: `/app/repairs`,
        });
        return;
      }

      // 4. Nothing found
      setResult({
        type: 'unknown',
        id: null,
        label: 'No matching item found',
        subLabel: `Code scanned: "${code}" — not matched in inventory, accessories, or repair jobs.`,
        url: null,
      });
    } catch (err) {
      // Not found errors from .single() are ok — fall through handled above
      setResult({
        type: 'unknown',
        id: null,
        label: 'No matching item found',
        subLabel: `Code: "${code}" — check if the stock tag matches a valid laptop Stock ID, accessory SKU, or repair job number.`,
        url: null,
      });
    } finally {
      setIsLooking(false);
    }
  }, []);

  const startScanning = useCallback(async () => {
    setError(null);
    setResult(null);
    setLastScanned(null);

    try {
      codeReader.current = new BrowserMultiFormatReader();
      const videoElement = videoRef.current;
      if (!videoElement) return;

      setScanning(true);

      const controls = await codeReader.current.decodeFromVideoDevice(
        undefined, // auto select camera
        videoElement,
        (scanResult, _err) => {
          if (scanResult) {
            const text = scanResult.getText();
            if (text !== lastScanned) {
              setLastScanned(text);
              void resolveCode(text);
            }
          }
        }
      );

      controlsRef.current = controls;
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message.includes('NotAllowed')
            ? 'Camera permission denied. Please allow camera access in your browser settings.'
            : err.message
          : 'Unable to start camera.';

      setError(message);
      setScanning(false);
    }
  }, [lastScanned, resolveCode]);

  const stopScanning = useCallback(() => {
    try {
      controlsRef.current?.stop();
    } catch (_) {}
    controlsRef.current = null;
    setScanning(false);
  }, []);

  const handleReset = () => {
    setResult(null);
    setLastScanned(null);
    setManualInput('');
  };

  const handleManualSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) void resolveCode(manualInput.trim());
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, [stopScanning]);

  const resultIcons = {
    laptop: <LaptopIcon className="w-8 h-8 text-brand-green" />,
    accessory: <Package className="w-8 h-8 text-blue-600" />,
    repair: <Wrench className="w-8 h-8 text-amber-600" />,
    unknown: <AlertTriangle className="w-8 h-8 text-red-500" />,
  };

  return (
    <div className="max-w-xl mx-auto space-y-5 text-xs">
      {/* Header */}
      <div>
        <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
          <ScanLine className="w-5 h-5 text-brand-green" />
          <span>Barcode & QR Scanner</span>
        </h1>
        <p className="text-brand-muted mt-0.5">
          Scan a laptop stock tag (QR/barcode), accessory SKU, or repair ticket to quickly look up its record.
        </p>
      </div>

      {/* Camera Preview */}
      <div className="bg-black rounded-xl overflow-hidden border border-brand-border relative aspect-video">
        <video
          ref={videoRef}
          className={`w-full h-full object-cover ${scanning ? 'opacity-100' : 'opacity-0'}`}
          muted
          playsInline
        />

        {!scanning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center space-y-3 text-white">
            <Camera className="w-10 h-10 opacity-50" />
            <p className="text-sm font-semibold opacity-70">Camera is off</p>
          </div>
        )}

        {/* Scanning Overlay Crosshair */}
        {scanning && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-48 h-32 border-2 border-brand-green rounded-md shadow-lg">
              <div className="absolute top-0 left-0 w-5 h-5 border-t-4 border-l-4 border-brand-green rounded-tl" />
              <div className="absolute top-0 right-0 w-5 h-5 border-t-4 border-r-4 border-brand-green rounded-tr" />
              <div className="absolute bottom-0 left-0 w-5 h-5 border-b-4 border-l-4 border-brand-green rounded-bl" />
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b-4 border-r-4 border-brand-green rounded-br" />
            </div>
          </div>
        )}
      </div>

      {/* Camera Controls */}
      <div className="flex items-center justify-center space-x-3">
        {!scanning ? (
          <button
            onClick={startScanning}
            className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-green text-white font-bold rounded-lg hover:bg-brand-green-hover transition-colors shadow-md"
          >
            <Camera className="w-4 h-4" />
            <span>Start Camera Scanner</span>
          </button>
        ) : (
          <button
            onClick={stopScanning}
            className="inline-flex items-center space-x-2 px-5 py-2.5 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-colors shadow-md"
          >
            <CameraOff className="w-4 h-4" />
            <span>Stop Camera</span>
          </button>
        )}

        {(result || lastScanned) && (
          <button
            onClick={handleReset}
            className="inline-flex items-center space-x-1 px-4 py-2.5 bg-white text-brand-muted border border-brand-border font-semibold rounded-lg hover:text-brand-black"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Clear</span>
          </button>
        )}
      </div>

      {/* Camera Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 font-semibold flex items-center space-x-2">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Manual Search Fallback */}
      <form onSubmit={handleManualSearch} className="flex gap-2">
        <input
          placeholder="Or type Stock ID, SKU, or Job Number manually..."
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          className="flex-1 px-3 py-2 bg-white border border-brand-border rounded-lg text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
        />
        <button
          type="submit"
          disabled={!manualInput.trim() || isLooking}
          className="px-4 py-2 bg-brand-green text-white font-bold rounded-lg hover:bg-brand-green-hover disabled:opacity-50"
        >
          <Search className="w-4 h-4" />
        </button>
      </form>

      {/* Lookup In Progress */}
      {isLooking && (
        <div className="flex items-center justify-center py-6 space-x-2 text-brand-muted">
          <Loader2 className="w-5 h-5 text-brand-green animate-spin" />
          <span className="font-semibold">Looking up code in database...</span>
        </div>
      )}

      {/* Scan Result Card */}
      {result && !isLooking && (
        <div
          className={`p-4 rounded-xl border-2 space-y-3 ${
            result.type === 'unknown'
              ? 'bg-red-50 border-red-300'
              : 'bg-green-50 border-brand-green'
          }`}
        >
          <div className="flex items-start space-x-3">
            <div className="shrink-0 mt-0.5">{resultIcons[result.type]}</div>
            <div className="flex-1">
              <p className="font-extrabold text-sm text-brand-black">{result.label}</p>
              <p className="text-brand-muted mt-0.5">{result.subLabel}</p>
              {lastScanned && (
                <p className="text-[10px] font-mono text-brand-muted mt-1">
                  Raw scan: <span className="text-brand-black">{lastScanned}</span>
                </p>
              )}
            </div>
          </div>

          {result.url && (
            <div className="pt-2 border-t border-brand-green/20 flex items-center justify-between">
              <span className="text-[11px] text-brand-muted font-semibold">
                Navigate to the full record:
              </span>
              <button
                onClick={() => navigate(result.url!)}
                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover text-xs"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Open Module</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Usage Instructions */}
      {!result && !isLooking && (
        <div className="bg-white border border-brand-border rounded-xl p-4 space-y-2">
          <p className="font-bold text-brand-black uppercase tracking-wider">How to Use</p>
          <ul className="space-y-1.5 text-brand-muted">
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-brand-green shrink-0 mt-0.5" />
              <span>Point camera at a laptop <strong className="text-brand-black">QR stock tag</strong> (e.g., LT-00001) to look it up instantly.</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-brand-green shrink-0 mt-0.5" />
              <span>Scan an accessory <strong className="text-brand-black">barcode SKU</strong> to see current stock quantity.</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-brand-green shrink-0 mt-0.5" />
              <span>Scan a <strong className="text-brand-black">repair job QR ticket</strong> to see the repair job status.</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-brand-green shrink-0 mt-0.5" />
              <span>Use the manual input above if camera scanning is not available.</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};
